import { Box, PressableOpacity } from "app/components/common";
import CameraIcon from "app/components/Icons/CameraIcon";
import ImageIcon from "app/components/Icons/ImageIcon";
import MicIcon from "app/components/Icons/MicIcon";
import CameraVideoIcon from "app/components/Icons/Video/CameraVideoIcon";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import React, { useState, useRef } from "react";
import {
  StyleSheet,
  View,
  LayoutAnimation,
  TextInput,
  UIManager,
  Keyboard,
} from "react-native";

import ArrowLeftSlider from "app/components/Icons/ArrowSlider/ArrowLeftSlider";
import {
  isAndroid,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import { useSelector } from "react-redux";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import UploadPreview from "../FeedbackScreen/UploadPreview";
import AudioRecordingBar from "./ChatScreen/AudioRecordingBar";
import hasAudioExtension from "@microdoing/shared/utils/hasAudioExtension";
import Animated, {
  useAnimatedKeyboard,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import ChatSend from "./ChatScreen/ChatSend";
import hasVideoExtension from "@microdoing/shared/utils/hasVideoExtension";
import hasImageExtension from "@microdoing/shared/utils/hasImageExtension";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const ReanimatedBox = Animated.createAnimatedComponent(Box);

const ORIGIN_INPUT_HEIGHT = isWebDesktop ? 35 : 28;

interface ContentSize {
  width: number;
  height: number;
}

type NativeElement = {
  nativeEvent: {
    contentSize: ContentSize;
  };
};

const CustomLayoutSpring = {
  duration: 200,
  create: {
    type: LayoutAnimation.Types.easeOut,
    property: LayoutAnimation.Properties.opacity,
    springDamping: 0.7,
  },
  update: {
    type: LayoutAnimation.Types.easeOut,
    springDamping: 0.7,
  },
  delete: {
    type: LayoutAnimation.Types.easeOut,
    property: LayoutAnimation.Properties.opacity,
    springDamping: 0.7,
  },
};

const styles = StyleSheet.create({
  composer: {
    flex: 1,
    position: "relative",
    justifyContent: "flex-start",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: theme.colors["neutral.50"],
    borderWidth: 1,
    borderColor: theme.colors["neutral.100"],
    marginLeft: normalize(10),
    marginRight: normalize(10),
    marginBottom: normalize(5),
    paddingTop: normalize(6),
    paddingBottom: normalize(0),
    paddingLeft: normalize(8),
    paddingRight: normalize(8),
    minHeight: isWebDesktop ? 0 : normalize(38),
    maxHeight: normalize(118),
    transition: "all 0.2s ease-in-out",
  },
  textInput: {
    ...theme.body["l4-medium"],
    flex: undefined,
    // lineHeight: normalize(22),
    color: theme.colors["neutral.900"],
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: "transparent",
    borderColor: "transparent",
    minHeight: 55,
    height: 106,
    maxHeight: 106,
    textAlignVertical: "top",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    outlineWidth: 0,
  },
  iconChat: {
    height: normalize(32),
    width: normalize(32),
    padding: theme.spacing["xxs"],
    alignContent: "center",
    justifyContent: "center",
  },
});

interface Props {
  text: string;
  placeholder: string;
  placeholderTextColor: string;
  textInputProps: {};
  onTextChanged: (text: string) => void;
  multiline: boolean;
  disableComposer: boolean;
  textInputAutoFocus: boolean;
  keyboardAppearance: "default" | "light" | "dark" | undefined;
  handlePressAudio: () => void;
  openNativeModule: (module: string, selectionLimit?: number) => () => void;
  handleCancelSelection: () => void;
  isAudioActivated: boolean;
  cancelAudio: () => void;
  sendAudio: () => void;
  audioDuration: number;
  onSend: () => any;
}

export const InputChat = ({
  text = "",
  placeholder = "Type a message...",
  placeholderTextColor,
  textInputProps,
  onTextChanged,
  multiline = true,
  disableComposer = false,
  textInputAutoFocus,
  keyboardAppearance,
  openNativeModule,
  handleCancelSelection,
  handlePressAudio,
  isAudioActivated,
  audioDuration,
  sendAudio,
  cancelAudio,
  onSend,
}: Props) => {
  if (isAndroid) {
    UIManager.setLayoutAnimationEnabledExperimental &&
      UIManager.setLayoutAnimationEnabledExperimental(true);
  }

  const inputRef: any = useRef(null);
  const [newContentSize, setNewContentSize] = useState<
    ContentSize | undefined
  >();
  const [finalInputHeight, setFinalInputHeight] = useState(ORIGIN_INPUT_HEIGHT);
  const [isOptionDisplayed, setIsOptionDisplayed] = useState(true);

  const filesToUpload = useSelector(getFilesToUpload);

  const keyboard = useAnimatedKeyboard();
  const insets = useSafeAreaInsets();

  const customOnSendMessage = () => {
    if ((text || filesToUpload.length > 0) && onSend) {
      const fileToUpload = filesToUpload?.[0] || {
        uri: "",
        fileName: "",
        filePreview: "",
      };
      const { uri = "", fileName = "", preview = "" } = fileToUpload;
      const hasVideo = hasVideoExtension(fileName);
      const hasImage = hasImageExtension(fileName);

      const video = hasVideo ? uri : undefined;
      let image = hasImage ? uri : undefined;
      if (video) {
        image = preview;
      }

      return onSend({ text: text?.trim() || "", video, image }, true);
    }
    return;
  };

  const handleKeyPress = event => {
    if (!isWebDesktop) return;

    if (event.key === "Enter" && !event.shiftKey && !event.ctrlKey) {
      // Handle enter key press here
      event.preventDefault();
      event.stopPropagation();
      customOnSendMessage();
    }
  };

  const animatedInputStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: keyboard.height.value
            ? -keyboard.height.value
            : withTiming(!!insets.bottom ? -25 : 1),
        },
      ],
    };
  }, []);

  const calcInputHeight = (contentSize: ContentSize) => {
    if (contentSize?.height) {
      if (!text?.length && finalInputHeight) {
        LayoutAnimation.configureNext(CustomLayoutSpring);
        setFinalInputHeight(ORIGIN_INPUT_HEIGHT);
        return;
      }
      LayoutAnimation.configureNext(CustomLayoutSpring);
      setFinalInputHeight(contentSize.height + 14);
    }
  };

  const onContentSizeChange = ({
    nativeEvent: { contentSize },
  }: NativeElement) => {
    if (isWebDesktop) {
      const chatList = document?.getElementById("chat-list");
      chatList.style.marginBottom = `${contentSize.height}px`;
      chatList.style.transition = "margin-bottom 0.2s ease-in-out";
      if (!text?.length) {
        chatList.style.marginBottom = `0px`;
        setFinalInputHeight(ORIGIN_INPUT_HEIGHT);
      } else {
        setFinalInputHeight(contentSize.height + 4);
      }
      return;
    }

    if (!contentSize) {
      return;
    }

    if (
      !newContentSize ||
      (!!newContentSize && newContentSize.height !== contentSize.height)
    ) {
      setNewContentSize(contentSize);
      if (!text?.length) {
        LayoutAnimation.configureNext(CustomLayoutSpring);
        setFinalInputHeight(ORIGIN_INPUT_HEIGHT);
      } else {
        calcInputHeight(contentSize);
      }
    }
  };

  const onChangeText = (newText: string) => {
    if ((isOptionDisplayed && !!newText) || (!text && !!newText)) {
      LayoutAnimation.configureNext(CustomLayoutSpring);

      setIsOptionDisplayed(false);
    } else if (!newText) {
      setFinalInputHeight(ORIGIN_INPUT_HEIGHT);
    }

    if (!!onTextChanged) {
      onTextChanged(newText);
    }
  };

  const handleHideOption = () => {
    LayoutAnimation.configureNext(CustomLayoutSpring);
    setIsOptionDisplayed(false);
  };

  const handleShowOption = () => {
    LayoutAnimation.configureNext(CustomLayoutSpring);
    setIsOptionDisplayed(true);
  };

  const isAudio = hasAudioExtension(
    filesToUpload?.[0]?.uri,
    filesToUpload?.[0]?.fileName,
  );

  const handleOpenGallery = () => {
    Keyboard.dismiss();
    openNativeModule("gallery", 1)();
  };

  if (isAudioActivated)
    return (
      <Box
        flex={isWebDesktop ? 0 : 1}
        position="absolute"
        width="100%"
        bottom={0}
        borderTopWidth={1}
        borderTopColor="neutral.350"
      >
        <AudioRecordingBar
          audioDuration={audioDuration}
          cancelAudio={cancelAudio}
          sendAudio={sendAudio}
        />
      </Box>
    );

  return (
    <ReanimatedBox
      backgroundColor="neutral.white"
      flexDirection="column"
      flex={1}
      position="absolute"
      width="100%"
      bottom={0}
      style={animatedInputStyle}
    >
      <Box flexDirection="row" width="100%" flex={1} justifyContent="center">
        <Box
          flexDirection="row"
          height={filesToUpload.length > 0 ? 50 : 38}
          marginTop="xs"
          width="auto"
          paddingLeft={filesToUpload.length > 0 ? "s" : "xxs"}
        >
          {filesToUpload.length > 0 && !isAudio && (
            <UploadPreview
              {...filesToUpload[0]}
              previewSize={normalize(30)}
              cancelSelection={handleCancelSelection}
            />
          )}
          {filesToUpload.length < 1 && (
            <>
              {text !== "" && !isOptionDisplayed && (
                <PressableOpacity
                  onPress={handleShowOption}
                  style={{ alignItems: "center", justifyContent: "center" }}
                >
                  <ArrowLeftSlider color={theme.colors["brand.500"]} />
                </PressableOpacity>
              )}
              {(text === "" || isOptionDisplayed) && (
                <>
                  {!isWeb && openNativeModule && (
                    <PressableOpacity
                      style={styles.iconChat}
                      onPress={openNativeModule("video")}
                    >
                      <CameraVideoIcon
                        width={normalize(24)}
                        height={normalize(24)}
                        color={theme.colors["brand.500"]}
                      />
                    </PressableOpacity>
                  )}
                  {!isWeb && openNativeModule && (
                    <PressableOpacity
                      style={styles.iconChat}
                      onPress={openNativeModule("photo")}
                    >
                      <CameraIcon
                        width={normalize(24)}
                        height={normalize(24)}
                        color={theme.colors["brand.500"]}
                      />
                    </PressableOpacity>
                  )}
                  {!!handleOpenGallery && (
                    <PressableOpacity
                      style={styles.iconChat}
                      onPress={handleOpenGallery}
                    >
                      <ImageIcon
                        width={normalize(24)}
                        height={normalize(24)}
                        color={theme.colors["brand.500"]}
                      />
                    </PressableOpacity>
                  )}
                  {!!handlePressAudio && (
                    <PressableOpacity
                      style={styles.iconChat}
                      onPress={handlePressAudio}
                    >
                      <MicIcon
                        width={normalize(24)}
                        height={normalize(24)}
                        color={theme.colors["brand.500"]}
                      />
                    </PressableOpacity>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        <View
          style={[
            styles.composer,
            {
              borderWidth: 1,
              marginTop: finalInputHeight > 44 ? 3 : 6,
              height: finalInputHeight,
              opacity: filesToUpload.length > 0 ? 0 : 1,
            },
          ]}
        >
          <TextInput
            ref={inputRef}
            testID={placeholder}
            accessible
            accessibilityLabel={placeholder}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            placeholderTextColor={placeholderTextColor}
            multiline={multiline}
            editable={!disableComposer}
            onContentSizeChange={onContentSizeChange}
            onChangeText={onChangeText}
            textBreakStrategy="highQuality"
            style={styles.textInput}
            autoFocus={textInputAutoFocus}
            onFocus={handleHideOption}
            onPressIn={handleHideOption}
            onBlur={handleShowOption}
            value={text}
            autoCompleteType="off"
            enablesReturnKeyAutomatically
            underlineColorAndroid="transparent"
            keyboardAppearance={keyboardAppearance}
            {...textInputProps}
          />
        </View>
        <ChatSend
          text={text}
          onSend={customOnSendMessage}
          filesToUpload={filesToUpload}
        />
      </Box>
    </ReanimatedBox>
  );
};
